import { useTranslation } from '@/i18n'
import type { SldGraphView, SldGraphViews } from './sldGraphViews'

/**
 * The Parsed | Annotation switch on an SLD page (VISN-304). One control for both
 * editor chromes: the V2 navbar and the legacy header both render it.
 */
export default function SldViewSwitch({
  views,
  onSelect,
}: {
  views: SldGraphViews
  /** The view picked; the page resolves that view's current graph itself. */
  onSelect: (view: SldGraphView) => void
}) {
  const { t } = useTranslation()
  // Parsed first: the parse is what people open a drawing to see.
  const options: Array<[SldGraphView, string | null, string]> = [
    ['parsed', views.parsedGraphId, t('data_catalog:graph_editor.sld_view.parsed', { defaultValue: 'Parsed' })],
    ['annotation', views.annotationGraphId, t('data_catalog:graph_editor.sld_view.annotation', { defaultValue: 'Annotation' })],
  ]
  return (
    <div
      role="tablist"
      aria-label={t('data_catalog:graph_editor.sld_view.label', { defaultValue: 'SLD view' })}
      className="bg-[#f5f5f5] dark:bg-dark-accent flex shrink-0 items-center p-[3px] rounded-lg h-8"
    >
      {options.map(([view, targetId, label]) => {
        const isActive = views.view === view
        return (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={!targetId}
            title={
              !targetId && view === 'parsed'
                ? t('data_catalog:graph_editor.sld_view.parse_pending', {
                    defaultValue: 'No parse for this page yet',
                  })
                : undefined
            }
            onClick={() => {
              if (!isActive && targetId) onSelect(view)
            }}
            className={`px-3 text-sm font-medium whitespace-nowrap rounded-md transition-all h-full flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${
              isActive
                ? 'bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
